import { DEFAULT_DURATION_SETTINGS } from "@p0security/shared/configuration/constant";
import { DurationOption } from "@p0security/shared/configuration/types";
import {
  convertToDurationOption,
  sortApprovalOptions,
} from "@p0security/shared/permission-requests/util";
import { PermissionRequest } from "@p0security/shared/types/permission";
import { Button, Modal, Select, Typography } from "antd";
import { useDurationSettings } from "components/Admin/useDurationSettings";
import { useAuthFetch } from "components/Login/hook";
import { compact, uniqBy } from "lodash";
import { FirestoreDoc } from "providers/FirestoreProvider";
import { useCallback, useMemo, useState } from "react";

import {
  handleRequestActionResponse,
  requestDescription,
} from "../requestUtils";

const buildCustomRequestDurationOption = (
  duration: string | undefined
): DurationOption | undefined => {
  const customDuration = duration
    ? convertToDurationOption(duration)
    : undefined;

  return customDuration
    ? {
        ...customDuration,
        value: `${customDuration.value} (requested)`,
      }
    : undefined;
};

export const ApproveRequestModal: React.FC<{
  loading: boolean;
  request: FirestoreDoc<PermissionRequest> | undefined;
  onClose: () => void;
}> = ({ loading, request, onClose }) => {
  const optionsDoc = useDurationSettings();
  const [approvalLength, setApprovalLength] = useState<string>();
  const [approvalLoading, setApprovalLoading] = useState(false);
  const authFetch = useAuthFetch(undefined, setApprovalLoading);

  const baseOptions = useMemo(
    () =>
      [
        ...(optionsDoc.doc?.data.options ?? DEFAULT_DURATION_SETTINGS.options),
      ].sort(sortApprovalOptions),
    [optionsDoc]
  );

  const options = useMemo(
    () =>
      uniqBy(
        compact([
          buildCustomRequestDurationOption(request?.data.duration),
          ...baseOptions,
        ]).map((option) => ({
          value: `${option.time}${option.unit}`,
          label: option.value,
        })),
        (option) => option.value
      ),
    [request, baseOptions]
  );

  const updateRequestDuration = useCallback(
    (value: string) => setApprovalLength(value),
    []
  );

  const handleApprove = useCallback(
    async (requestId: string) => {
      const response = await authFetch(
        `permission-requests/${requestId}/approve`,
        {
          method: "POST",
          json: {
            expirationLength: approvalLength || options[0].value,
          },
        }
      );
      handleRequestActionResponse(response, "Request approved");
      if (response?.ok) {
        onClose();
      }
    },
    [approvalLength, options, authFetch, onClose]
  );

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const approveRequest = useCallback(
    () => request && handleApprove(request.id),
    [handleApprove, request]
  );

  return (
    <Modal
      title="Approve request"
      open={request !== undefined}
      onCancel={closeModal}
      footer={
        request && [
          <Button
            key="submit"
            type="primary"
            onClick={approveRequest}
            loading={loading || approvalLoading}
          >
            Approve
          </Button>,
        ]
      }
    >
      {request ? (
        <>
          <Typography.Paragraph>
            For how long would you like to grant{" "}
            <Typography.Text underline>
              {requestDescription(request.data)}
            </Typography.Text>{" "}
            to <Typography.Text code>{request.data.principal}</Typography.Text>?
          </Typography.Paragraph>
          <Select
            style={{ width: "100%" }}
            options={options}
            value={approvalLength || options[0].value}
            onChange={updateRequestDuration}
          />
        </>
      ) : (
        <Typography.Paragraph>No request selected</Typography.Paragraph>
      )}
    </Modal>
  );
};
